import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { resolveLanding } from "@/lib/roleContext";
import "./LandingPageV3.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REMEMBER_KEY = "ipolabs_login_remember_v1";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const EXISTING_ROLES = [
  { id: "admin", label: "Admin", icon: "ti-shield-lock" },
  { id: "employee", label: "Employee", icon: "ti-users" },
  { id: "existing_user", label: "Existing User", icon: "ti-user-check" },
];

const GoogleIcon = () => (
  <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();

  // step: "choose" | "existing" | "new"
  const [step, setStep] = useState("choose");
  const [role, setRole] = useState("existing_user");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingName, setPendingName] = useState("");

  // duplicate flags (new-user)
  const [emailDup, setEmailDup] = useState(false);
  const [mobileDup, setMobileDup] = useState(false);

  // forgot password dialog
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotResult, setForgotResult] = useState(null);

  // strict New-vs-Existing rejection pop-up ({title, msg})
  const [notice, setNotice] = useState(null);

  // restore remembered identity
  useEffect(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_KEY);
      if (raw) {
        const r = JSON.parse(raw);
        if (r?.email) setEmail(r.email);
        if (r?.role) setRole(r.role);
      }
    } catch { /* ignore */ }
  }, []);

  // pop-up when a Google sign-in was rejected by the strict New/Existing rules
  useEffect(() => {
    const n = new URLSearchParams(window.location.search).get("notice");
    if (!n) return;
    if (n === "exists") {
      setStep("existing");
      setNotice({ title: "Account already exists", msg: "This Google account is already registered with IPOLabs. Please continue as an Existing User." });
    } else if (n === "notfound") {
      setStep("new");
      setNotice({ title: "No account found", msg: "We couldn't find an IPOLabs account for that Google email. Please create a New User account to continue." });
    }
    window.history.replaceState(null, "", "/login");
  }, []);

  // handle Google OAuth callback
  useEffect(() => {
    if (typeof window === "undefined" || !window.location.hash?.includes("session_id=")) return;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    if (!sessionId) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.session_token) localStorage.setItem("session_token", data.session_token);
          window.history.replaceState(null, "", "/login");
          try { sessionStorage.removeItem("ipolabs_consent_acked"); } catch (_) { /* ignore */ }
          if (data.needs_mobile) {
            setPendingName(data.user?.name || "");
            setStep("google-mobile");
          } else {
            const dest = await resolveLanding(data.user);
            navigate(dest);
          }
        }
      } catch { /* ignore */ }
    })();
  }, [navigate]);

  const goChoose = () => { setStep("choose"); setError(""); setEmailDup(false); setMobileDup(false); };

  const persistRemember = () => {
    try { localStorage.setItem(REMEMBER_KEY, JSON.stringify({ email, role })); } catch { /* ignore */ }
  };

  const google = (flow) => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const r = `${window.location.origin}/login?flow=${flow}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(r)}`;
  };

  // ── New user: live duplicate checks ──────────────────────────────────
  const checkEmailDup = async () => {
    if (!email || !email.includes("@")) { setEmailDup(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/auth/check-email`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      setEmailDup(!!data.exists);
    } catch { /* ignore */ }
  };

  const checkMobileDup = async () => {
    if (!mobile || mobile.length < 10) { setMobileDup(false); return; }
    try {
      const res = await fetch(`${API_URL}/api/auth/lookup-mobile`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile }),
      });
      setMobileDup(res.ok); // 200 = found (duplicate), 404 = available
    } catch { /* ignore */ }
  };

  // ── Submit: existing user login ──────────────────────────────────────
  const submitExisting = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Email and password are required"); return; }
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ email, password, login_role: role }),
      });
      const data = await res.json();
      if (!res.ok) { setError(formatApiErrorDetail(data.detail) || "Login failed"); setLoading(false); return; }
      if (data.session_token) localStorage.setItem("session_token", data.session_token);
      persistRemember();
      try { sessionStorage.removeItem("ipolabs_consent_acked"); } catch (_) { /* ignore */ }
      const dest = await resolveLanding(data.user);
      navigate(dest, { state: { user: data.user } });
    } catch { setError("Connection error"); }
    setLoading(false);
  };

  // ── Submit: new user registration ────────────────────────────────────
  const submitNew = async (e) => {
    e.preventDefault();
    if (emailDup || mobileDup) { return; }
    if (!name || !email || !password) { setError("Name, email and password are required"); return; }
    if (!mobile || mobile.length < 10) { setError("A valid mobile number is required"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ email, password, name, mobile }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = formatApiErrorDetail(data.detail);
        if (res.status === 409 && /already exists/i.test(msg)) {
          setStep("existing");
          setNotice({ title: "Account already exists", msg });
          setLoading(false);
          return;
        }
        if (/mobile/i.test(msg)) setMobileDup(true);
        else if (/email/i.test(msg)) setEmailDup(true);
        else setError(msg || "Registration failed");
        setLoading(false);
        return;
      }
      if (data.session_token) localStorage.setItem("session_token", data.session_token);
      persistRemember();
      try { sessionStorage.removeItem("ipolabs_consent_acked"); } catch (_) { /* ignore */ }
      toast.success("Welcome to IPOLabs! Your account is ready.");
      navigate("/home", { state: { user: data.user } });
    } catch { setError("Connection error"); }
    setLoading(false);
  };

  // ── Google new-user: capture mobile on first login ──────────────────
  const submitGoogleMobile = async () => {
    if (!mobile || mobile.length < 10) { setError("A valid mobile number is required"); return; }
    if (mobileDup) return;
    setError(""); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/save-mobile`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ mobile }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = formatApiErrorDetail(data.detail);
        if (/mobile/i.test(msg)) setMobileDup(true); else setError(msg || "Could not save mobile");
        setLoading(false); return;
      }
      navigate("/home");
    } catch { setError("Connection error"); }
    setLoading(false);
  };

  // ── Forgot password handlers ─────────────────────────────────────────
  const handleForgotSubmit = async () => {
    setForgotLoading(true);
    try {
      const body = { email: forgotEmail };
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(formatApiErrorDetail(data.detail) || "Something went wrong"); setForgotLoading(false); return; }
      setForgotResult(data.message || "If an account with that email exists, a password reset link has been sent.");
    } catch { toast.error("Connection error"); }
    setForgotLoading(false);
  };

  const closeForgot = () => {
    setShowForgot(false); setForgotResult(null);
    setForgotEmail("");
  };

  const dupFlag = (step === "new" && (emailDup || mobileDup)) ? (
    <div className="auth-flag" data-testid="auth-dup-flag">
      <i className="ti ti-alert-triangle-filled" aria-hidden="true" />
      <div>
        This {emailDup && mobileDup ? "email and mobile number are" : emailDup ? "email is" : "mobile number is"} already
        registered with IPOLabs. Please{" "}
        <button type="button" onClick={() => { setShowForgot(true); }} data-testid="auth-flag-forgot">use Forgot Password</button>{" "}
        to recover your login details.
      </div>
    </div>
  ) : null;

  return (
    <div className="lpv3 auth" data-testid="login-page">
      {/* ── LEFT PANE (constant branding) ── */}
      <aside className="auth-left">
        <span className="blob1" /><span className="blob2" />
        <img src="/ipolabs-logo.png" alt="IPOLabs" className="auth-logo" onClick={() => navigate("/")} data-testid="login-logo" />
        <div className="auth-left-h">IPOLabs can help take your company <span>public</span></div>
        <div className="auth-left-d">IPOLabs provides a single platform that covers IPO Process Management, DRHP Build, Pre &amp; Post IPO Funding and Hiring IPO Subject Matter Experts.</div>
        <div className="auth-feats">
          {[
            { ic: "ti-shield-check", t: "SEBI aligned IPO Process Management" },
            { ic: "ti-cash", t: "Get access to Pre-IPO, Bridge Funds to help through your IPO Process" },
            { ic: "ti-chart-bar", t: "Test your IPO Readiness & Gaps - Real-time, Anywhere" },
            { ic: "ti-cloud-lock", t: "Secured Cloud Storage for Critical Documents Storage & Use" },
          ].map((f, i) => (
            <div className="auth-feat" key={i}>
              <span className="fic"><i className={`ti ${f.ic}`} aria-hidden="true" /></span>
              <span>{f.t}</span>
            </div>
          ))}
        </div>
        <p className="auth-copy">© {new Date().getFullYear()} IPOLabs AI Private Limited</p>
      </aside>

      {/* ── RIGHT PANE (flow) ── */}
      <main className="auth-right">
        <div className="auth-card">
          <img src="/ipolabs-logo.png" alt="IPOLabs" className="auth-logo auth-mobile-logo" onClick={() => navigate("/")} />

          {/* STEP 1 — CHOOSE */}
          {step === "choose" && (
            <div data-testid="auth-choose">
              <div className="auth-h">Welcome to IPOLabs</div>
              <div className="auth-sub">Let&apos;s get you to the right place.</div>
              <div className="auth-choice">
                <button type="button" className="auth-choice-card" onClick={() => { setStep("new"); setError(""); }} data-testid="choose-new-user">
                  <span className="auth-choice-ic"><i className="ti ti-user-plus" aria-hidden="true" /></span>
                  <span>
                    <span className="auth-choice-h">New User</span>
                    <span className="auth-choice-d">Create your IPOLabs Account</span>
                  </span>
                  <i className="ti ti-chevron-right chev" aria-hidden="true" />
                </button>
                <button type="button" className="auth-choice-card" onClick={() => { setStep("existing"); setError(""); }} data-testid="choose-existing-user">
                  <span className="auth-choice-ic"><i className="ti ti-login-2" aria-hidden="true" /></span>
                  <span>
                    <span className="auth-choice-h">Existing User</span>
                    <span className="auth-choice-d">Sign in to your IPOLabs Account</span>
                  </span>
                  <i className="ti ti-chevron-right chev" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — EXISTING USER */}
          {step === "existing" && (
            <div data-testid="auth-existing">
              <button type="button" className="auth-back" onClick={goChoose} data-testid="auth-back-btn"><i className="ti ti-arrow-left" aria-hidden="true" /> Back</button>
              <div className="auth-h">Sign in</div>
              <div className="auth-sub">Choose your role to continue.</div>

              <div className="auth-roles" data-testid="role-selector">
                {EXISTING_ROLES.map((r) => (
                  <button key={r.id} type="button" className={`auth-role${role === r.id ? " on" : ""}`} onClick={() => { setRole(r.id); setError(""); }} data-testid={`role-btn-${r.id}`}>
                    <i className={`ti ${r.icon}`} aria-hidden="true" />{r.label}
                  </button>
                ))}
              </div>

              <form onSubmit={submitExisting}>
                <div className="auth-field">
                  <div className="auth-label">Email</div>
                  <div className="auth-input-wrap">
                    <i className="ti ti-mail auth-input-ic" aria-hidden="true" />
                    <input type="email" className="auth-input has-icon" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email" />
                  </div>
                </div>
                <div className="auth-field">
                  <div className="auth-label">Password
                    <button type="button" className="auth-forgot" onClick={() => setShowForgot(true)} data-testid="forgot-password-btn">Forgot password?</button>
                  </div>
                  <div className="auth-input-wrap">
                    <i className="ti ti-lock auth-input-ic" aria-hidden="true" />
                    <input type={showPw ? "text" : "password"} className="auth-input has-icon" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password" />
                    <button type="button" className="auth-eye" onClick={() => setShowPw(!showPw)}><i className={`ti ${showPw ? "ti-eye-off" : "ti-eye"}`} aria-hidden="true" /></button>
                  </div>
                </div>
                {error && <div className="auth-err" data-testid="login-error">{error}</div>}
                <button type="submit" className="auth-btn" disabled={loading} data-testid="login-submit">
                  {loading ? <i className="ti ti-loader-2" style={{ animation: "spin 1s linear infinite" }} aria-hidden="true" /> : <>Sign in <i className="ti ti-arrow-right" aria-hidden="true" /></>}
                </button>
              </form>

              <div className="auth-or">or</div>
              <button type="button" className="auth-google" onClick={() => google("existing")} data-testid="login-google"><GoogleIcon /> Sign in with Google</button>
            </div>
          )}

          {/* STEP 3 — NEW USER */}
          {step === "new" && (
            <div data-testid="auth-new">
              <button type="button" className="auth-back" onClick={goChoose} data-testid="auth-back-btn"><i className="ti ti-arrow-left" aria-hidden="true" /> Back</button>
              <div className="auth-h">Create your account</div>
              <div className="auth-sub">Join IPOLabs in under a minute.</div>

              <form onSubmit={submitNew} style={{ marginTop: 22 }}>
                {dupFlag}
                <div className="auth-field">
                  <div className="auth-label">Full name</div>
                  <div className="auth-input-wrap">
                    <i className="ti ti-user auth-input-ic" aria-hidden="true" />
                    <input className="auth-input has-icon" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya Sharma" data-testid="signup-name" />
                  </div>
                </div>
                <div className="auth-field">
                  <div className="auth-label">Mobile number</div>
                  <div className="auth-input-wrap">
                    <i className="ti ti-phone auth-input-ic" aria-hidden="true" />
                    <input className={`auth-input has-icon${mobileDup ? " err" : ""}`} value={mobile} onChange={(e) => { setMobile(e.target.value); setMobileDup(false); }} onBlur={checkMobileDup} placeholder="98765 43210" data-testid="signup-mobile" />
                  </div>
                </div>
                <div className="auth-field">
                  <div className="auth-label">Email</div>
                  <div className="auth-input-wrap">
                    <i className="ti ti-mail auth-input-ic" aria-hidden="true" />
                    <input type="email" className={`auth-input has-icon${emailDup ? " err" : ""}`} value={email} onChange={(e) => { setEmail(e.target.value); setEmailDup(false); }} onBlur={checkEmailDup} placeholder="you@example.com" data-testid="signup-email" />
                  </div>
                </div>
                <div className="auth-field">
                  <div className="auth-label">Password</div>
                  <div className="auth-input-wrap">
                    <i className="ti ti-lock auth-input-ic" aria-hidden="true" />
                    <input type={showPw ? "text" : "password"} className="auth-input has-icon" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create password (min 6 chars)" data-testid="signup-password" />
                    <button type="button" className="auth-eye" onClick={() => setShowPw(!showPw)}><i className={`ti ${showPw ? "ti-eye-off" : "ti-eye"}`} aria-hidden="true" /></button>
                  </div>
                </div>
                {error && <div className="auth-err" data-testid="signup-error">{error}</div>}
                <button type="submit" className="auth-btn" disabled={loading || emailDup || mobileDup} data-testid="signup-submit">
                  {loading ? <i className="ti ti-loader-2" style={{ animation: "spin 1s linear infinite" }} aria-hidden="true" /> : <>Create account <i className="ti ti-arrow-right" aria-hidden="true" /></>}
                </button>
              </form>

              <div className="auth-or">or</div>
              <button type="button" className="auth-google" onClick={() => google("new")} data-testid="signup-google"><GoogleIcon /> Sign up with Google</button>
            </div>
          )}

          {/* STEP — GOOGLE NEW USER: capture mobile */}
          {step === "google-mobile" && (
            <div data-testid="auth-google-mobile">
              <div className="auth-h">Almost there{pendingName ? `, ${pendingName.split(" ")[0]}` : ""}</div>
              <div className="auth-sub">Add your mobile number to complete your IPOLabs profile.</div>
              <div style={{ marginTop: 22 }}>
                {mobileDup && (
                  <div className="auth-flag" data-testid="gm-dup-flag">
                    <i className="ti ti-alert-triangle-filled" aria-hidden="true" />
                    <div>This mobile number is already linked to another account. Please{" "}
                      <button type="button" onClick={() => { setShowForgot(true); }} data-testid="gm-flag-forgot">use Forgot Password</button>.
                    </div>
                  </div>
                )}
                <div className="auth-field">
                  <div className="auth-label">Mobile number</div>
                  <div className="auth-input-wrap">
                    <i className="ti ti-phone auth-input-ic" aria-hidden="true" />
                    <input className={`auth-input has-icon${mobileDup ? " err" : ""}`} value={mobile} onChange={(e) => { setMobile(e.target.value); setMobileDup(false); }} onBlur={checkMobileDup} placeholder="98765 43210" data-testid="gm-mobile" />
                  </div>
                </div>
                {error && <div className="auth-err" data-testid="gm-error">{error}</div>}
                <button type="button" className="auth-btn" disabled={loading || mobileDup} onClick={submitGoogleMobile} data-testid="gm-submit">
                  {loading ? <i className="ti ti-loader-2" style={{ animation: "spin 1s linear infinite" }} aria-hidden="true" /> : <>Continue <i className="ti ti-arrow-right" aria-hidden="true" /></>}
                </button>
                <div className="auth-foot" style={{ marginTop: 14 }}>
                  <a onClick={() => navigate("/dashboard-new")} data-testid="gm-skip">Skip for now</a>
                </div>
              </div>
            </div>
          )}

          <div className="auth-foot" data-testid="login-legal-links">
            <a onClick={() => navigate("/disclaimer")}>Terms</a><span>·</span>
            <a onClick={() => navigate("/disclaimer")}>Cookies</a><span>·</span>
            <a onClick={() => navigate("/disclaimer")}>Privacy</a>
          </div>
        </div>
      </main>

      {/* ── Strict New/Existing rejection pop-up ── */}
      <Dialog open={!!notice} onOpenChange={(o) => { if (!o) setNotice(null); }}>
        <DialogContent className="max-w-sm" data-testid="auth-notice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <i className="ti ti-alert-circle" style={{ color: "#e11d48" }} aria-hidden="true" /> {notice?.title}
            </DialogTitle>
            <DialogDescription className="text-sm" data-testid="auth-notice-msg">{notice?.msg}</DialogDescription>
          </DialogHeader>
          <button onClick={() => setNotice(null)} className="auth-btn" style={{ height: 40 }} data-testid="auth-notice-ok">Got it</button>
        </DialogContent>
      </Dialog>

      {/* ── Forgot Password Dialog ── */}
      <Dialog open={showForgot} onOpenChange={closeForgot}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <i className="ti ti-key" style={{ color: "#6C4BF4" }} aria-hidden="true" /> Forgot Password
            </DialogTitle>
            <DialogDescription className="text-xs">Enter your email address used for login and email a secure reset link.</DialogDescription>
          </DialogHeader>

          {forgotResult ? (
            <div className="py-4 text-center" data-testid="forgot-sent-message">
              <i className="ti ti-mail-fast" style={{ color: "#16a34a", fontSize: 40 }} aria-hidden="true" />
              <p className="text-sm font-semibold text-gray-800 mt-2">Check your email</p>
              <p className="text-xs text-gray-500 mt-1">{forgotResult}</p>
              <p className="text-[11px] text-gray-400 mt-2">The reset link is valid for 1 hour.</p>
              <button onClick={closeForgot} className="auth-btn" style={{ marginTop: 16, height: 40 }} data-testid="forgot-done-btn">Back to Login</button>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <label htmlFor="forgot-email" className="block text-xs font-semibold text-gray-700">Registered email address</label>
              <input
                id="forgot-email"
                type="email"
                autoFocus
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleForgotSubmit(); }}
                placeholder="you@company.com"
                className="auth-input"
                style={{ height: 46, fontSize: 15, border: "2px solid #d8d4ee", background: "#fff", borderRadius: 10 }}
                data-testid="forgot-email-input"
              />
              <button onClick={handleForgotSubmit} disabled={forgotLoading} className="auth-btn" style={{ height: 44, width: "100%", marginTop: 4 }} data-testid="forgot-submit-btn">
                {forgotLoading ? <i className="ti ti-loader-2" style={{ animation: "spin 1s linear infinite" }} /> : "Send Reset Link"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
